import { useAppStore } from "@hooks/useAppStore";
import { useEffect } from "react";
import { isIntroScreen } from "../helpers/intro-screens";
import { deriveItemTimingPlan } from "../helpers/item-timing-plan";
import useCurrentSession from "./useCurrentSession";
import useItemAnsweredState from "./useItemAnsweredState";
import usePausableCountdownWindow from "./usePausableCountdownWindow";
import useResponseControlsStore from "./useResponseControlsStore";
import useCountDown from "./useTimer";

const useFormativeItemTimer = (showDialog?: boolean) => {
	const showTimerConfig = useAppStore((s) => Boolean(s.sessionView?.timer));
	const { currentItem } = useCurrentSession();
	const currentItemId = currentItem?.id;
	const { isAnswered, isSummativeTest, inReviewMode } =
		useItemAnsweredState(currentItem);

	const {
		timeLimitSeconds,
		timerWaitsForAudioCompletion,
		micDelaySecondsAfterAudio,
		prepSecondsBeforeTimer,
	} = deriveItemTimingPlan({
		item: currentItem,
		isSummativeTest,
		inReviewMode,
	});

	const isItemAudioComplete = useResponseControlsStore((s) =>
		Boolean(currentItemId && s.audioCompletedItems[currentItemId]),
	);
	const awaitingAudioCompletion =
		timerWaitsForAudioCompletion && !isItemAudioComplete;

	const isHoldingForMicDelay = usePausableCountdownWindow({
		shouldHold: micDelaySecondsAfterAudio > 0 && isItemAudioComplete,
		holdSeconds: micDelaySecondsAfterAudio,
		resetKey: currentItemId,
		isPaused: showDialog,
	});

	const isHoldingForPrep = usePausableCountdownWindow({
		shouldHold: prepSecondsBeforeTimer > 0,
		holdSeconds: prepSecondsBeforeTimer,
		resetKey: currentItemId,
		isPaused: showDialog,
	});

	const showTimer =
		showTimerConfig &&
		!isSummativeTest &&
		!inReviewMode &&
		!isIntroScreen(currentItem) &&
		!isAnswered &&
		typeof timeLimitSeconds === "number" &&
		timeLimitSeconds > 0;

	const shouldRun =
		showTimer &&
		!showDialog &&
		!awaitingAudioCompletion &&
		!isHoldingForMicDelay &&
		!isHoldingForPrep;

	const { minutes, seconds, isNegative, start, pause } = useCountDown(
		(timeLimitSeconds ?? 0) * 1000,
		shouldRun,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: restart only on item change
	useEffect(() => {
		if (!showTimer) {
			pause();
			return;
		}
		start((timeLimitSeconds ?? 0) * 1000);
		if (!shouldRun) {
			pause();
		}
	}, [currentItemId]);

	return {
		showTimer,
		isOverrun: showTimer && isNegative,
		formattedTime: `${isNegative ? "-" : ""}${minutes}:${seconds}`,
	};
};

export default useFormativeItemTimer;
